import React from 'react';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import { FaqList } from '@/components/FaqSection';
import { MDX_COMPONENTS } from '@/components/resources/mdxComponents';
import {
  Breadcrumbs, ClusterChip, ArticleMeta, ShortAnswer, Toc, MobileToc, StickyRail,
  Sources, RelatedArticles, ShareRow,
} from '@/components/resources/parts';
import { RailCta, StartupCard } from '@/components/resources/RailCards';
import { TAX_LINE } from '@/lib/pricing';
import { usePageContext } from '../../pages/PageContextProvider.jsx';

// Every article body, compiled by @mdx-js/rollup. Each module's default export
// is the body; `toc` comes from remark-heading-ids. Eager so the body renders
// in the pre-rendered HTML and hydrates without a second request.
const BODIES = import.meta.glob('/content/resources/*.mdx', { eager: true });

const Article = () => {
  const { data } = usePageContext();
  const { article: a, reviewer, related } = data;
  const mod = BODIES[`/content/resources/${a.slug}.mdx`];
  const Body = mod.default;
  const toc = mod.toc || [];
  const fullUrl = `https://gabsor.com${a.url}`;

  return (
    <div className="min-h-screen bg-white">
      <Navigation transparent={false} />

      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 pb-20">
        <Breadcrumbs items={[{ name: 'Home', url: '/' }, { name: 'Resources', url: '/resources/' }, { name: a.title, url: a.url }]} />

        {/* Body and rail share the first grid row, so the sticky rail stops at
            the end of the body instead of running over the FAQ and footer. */}
        <article className="rs-article mt-8 lg:grid lg:grid-cols-[minmax(0,1fr)_var(--rs-rail)] lg:gap-x-(--rs-gap)">
          <div className="rs-body min-w-0">
            <header className="max-w-[68ch]">
              <ClusterChip cluster={a.cluster} />
              <h1 className="mt-3 text-[32px] leading-[40px] sm:text-[40px] sm:leading-[48px] font-bold tracking-tight text-gray-900">{a.title}</h1>
              <div className="mt-3 text-lg leading-relaxed text-gray-600">{a.description}</div>
              <div className="mt-4"><ArticleMeta reviewer={reviewer} updated={a.updated} reviewed={a.reviewed} readingTime={a.readingTime} /></div>
              <div className="mt-6"><ShortAnswer answer={a.answer} /></div>
              <div className="mt-4"><MobileToc toc={toc} /></div>
            </header>

            <div className="rs-prose mt-8">
              <Body components={MDX_COMPONENTS} />
            </div>
            {/* Tax footnote, once per page: under the "What it costs" card
                (the last block of the body), or in the rail card when the
                article has no <PricingSummary>. */}
            {a.pricingSummary && <p className="rs-tax-line">{TAX_LINE}</p>}

            {/* Below lg the rail is hidden: only the Startup card, at the end of the body. */}
            <div className="lg:hidden mt-10 max-w-[68ch]"><StartupCard /></div>
          </div>

          <aside className="hidden lg:block" aria-label="Article tools">
            <StickyRail>
              <Toc toc={toc} />
              <RailCta service={a.service} taxLine={!a.pricingSummary} />
              <StartupCard />
            </StickyRail>
          </aside>

          <div className="min-w-0 max-w-[68ch] mt-14 space-y-12 lg:col-start-1">
            {a.faq.length > 0 && (
              <section aria-labelledby="faq">
                <h2 id="faq" className="text-xl font-bold text-gray-900 mb-4">Frequently asked questions</h2>
                <FaqList items={a.faq.map((f) => ({ question: f.q, answer: f.a }))} defaultOpen bordered={false} />
              </section>
            )}
            <Sources sources={a.sources} />
            <RelatedArticles items={related} flat />
            <ShareRow url={fullUrl} title={a.title} />
          </div>
        </article>
      </main>

      <Footer />
    </div>
  );
};

export default Article;
